import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { BrandedLayout } from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import type { MimicState } from "../../stores/mimic";
import type { BrandColor, MemberRole, User } from "../../types";
import { isAdmin } from "../../types";

type OrgChartNode = {
  id: string;
  name: string;
  title: string | null;
  role: MemberRole;
  profilePictureUrl: string | null;
  managerId: string | null;
};

type TreeNode = OrgChartNode & { children: TreeNode[] };

type OrgChartPageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  isOrgAdmin: boolean;
  hasReports: boolean;
  nodes: OrgChartNode[];
};

export async function load(args: LoaderArgs): Promise<OrgChartPageState> {
  const orgId = args.params.orgId ?? "";

  const [authResult, orgsResult, orgResult, chartResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
    rpc.orgChart.getOrgTree({ workspaceId: orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const currentUser = authResult.user as User;
  const currentMember = orgResult.currentMember;
  const isOrgAdmin = isAdmin(currentMember.role);

  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  return {
    currentUser,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    isOrgAdmin,
    hasReports: orgFromList?.hasReports ?? false,
    nodes: chartResult.nodes,
  };
}

function buildTree(nodes: OrgChartNode[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  for (const n of nodes) {
    map.set(n.id, { ...n, children: [] });
  }

  const roots: TreeNode[] = [];
  for (const n of map.values()) {
    if (n.managerId && map.has(n.managerId)) {
      map.get(n.managerId)!.children.push(n);
    } else {
      roots.push(n);
    }
  }

  return roots;
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Org Chart");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    isOrgAdmin,
    hasReports,
    nodes,
  } = state.value;

  const tree = buildTree(nodes);

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="org-chart"
      isAdmin={isOrgAdmin}
      hasReports={hasReports}
    >
      <div class="flex flex-col gap-6">
        <div class="flex items-center justify-between">
          <h1 class="text-xl font-bold text-text">Org Chart</h1>
          <span class="text-sm text-text-muted">
            {nodes.length} {nodes.length === 1 ? "member" : "members"}
          </span>
        </div>

        {nodes.length === 0 ? (
          <div class="bg-surface border border-border rounded-lg p-8 text-center">
            <p class="text-text-muted">No members in this workspace yet.</p>
          </div>
        ) : (
          <div class="bg-surface border border-border rounded-lg py-3 px-4">
            {tree.map((root) => (
              <OutlineNode key={root.id} node={root} orgId={orgId} depth={0} />
            ))}
          </div>
        )}
      </div>
    </BrandedLayout>
  );
}

function OutlineNode({
  node,
  orgId,
  depth,
}: {
  node: TreeNode;
  orgId: string;
  depth: number;
}) {
  const collapsed = useSignal(false);
  const hasChildren = node.children.length > 0;
  const descendantCount = hasChildren ? countDescendants(node) : 0;

  return (
    <div>
      <div
        class="flex items-center gap-2 py-1 group"
        style={{ paddingLeft: `${depth * 24}px` }}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            type="button"
            onClick={() => {
              collapsed.value = !collapsed.value;
            }}
            class="w-5 h-5 flex items-center justify-center text-text-muted hover:text-text transition-colors shrink-0"
          >
            <span class="text-xs">{collapsed.value ? "+" : "\u2212"}</span>
          </button>
        ) : (
          <span class="w-5 h-5 shrink-0" />
        )}

        {/* Name + title */}
        <a
          href={`/orgs/${orgId}/members/${node.id}`}
          class="flex items-baseline gap-2 min-w-0 hover:underline"
        >
          <span class="text-sm font-medium text-text truncate">
            {node.name}
          </span>
          {node.title && (
            <span class="text-xs text-text-muted truncate shrink-0">
              {node.title}
            </span>
          )}
        </a>

        {/* Collapsed count */}
        {hasChildren && collapsed.value && (
          <span class="text-xs text-text-subtle shrink-0">
            ({descendantCount})
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && !collapsed.value && (
        <div>
          {node.children.map((child) => (
            <OutlineNode
              key={child.id}
              node={child}
              orgId={orgId}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function countDescendants(node: TreeNode): number {
  let count = node.children.length;
  for (const child of node.children) {
    count += countDescendants(child);
  }
  return count;
}
